export default class HistoricalDataManager {
  constructor(controller) {
    this.controller = controller;
    this.historicalFetchController = null;
    this.pendingHistoricalTime = null;
  }

  initialize() {
    if (!this.controller.hasTimeSliderTarget) return false;

    const baseDate = this.controller.hasBaseDateValue ? new Date(this.controller.baseDateValue) : new Date();
    const startOfDay = new Date(baseDate);
    startOfDay.setHours(0, 0, 0, 0);
    this.controller.sliderStartOfDay = startOfDay;

    const slotDurationMs = 15 * 60 * 1000;
    const now = new Date();

    if (this.controller.isTodayValue) {
      this.liveSlotIndex = Math.min(Math.floor((now - startOfDay) / slotDurationMs), this.totalSlots);
    } else {
      this.liveSlotIndex = this.totalSlots;
    }

    this.controller.timeSliderTarget.max = this.totalSlots;
    this.controller.timeSliderTarget.value = this.liveSlotIndex;
    this.updateSliderTrackColors();

    const urlParams = new URLSearchParams(window.location.search);
    const timeParam = urlParams.get("t");

    if (timeParam) {
      const slotIndex = this.parseTimeParam(timeParam, this.totalSlots);
      if (slotIndex !== null) {
        this.controller.timeSliderTarget.value = slotIndex;
        const selectedTime = this.slotIndexToTime(slotIndex);
        this.controller.selectedTimestampValue = selectedTime.toISOString();
        const isFuture = this.controller.isTodayValue && slotIndex > this.liveSlotIndex;
        this.updateTimeDisplay(selectedTime, isFuture);
        this.updateSliderTrackColors();
        this.fetchHistoricalActuals(selectedTime, { isFuture });
        return true;
      }
    }

    return false;
  }

  get totalSlots() {
    return 95;
  }

  initializeHistoricalDate() {
    const selectedTime = this.slotIndexToTime(this.liveSlotIndex);
    this.controller.selectedTimestampValue = selectedTime.toISOString();
    this.updateTimeDisplay(selectedTime);
    this.fetchHistoricalActuals(selectedTime);
  }

  timeSliderChanged() {
    const slotIndex = parseInt(this.controller.timeSliderTarget.value);
    const isFuture = this.controller.isTodayValue && slotIndex > this.liveSlotIndex;

    if (slotIndex === this.liveSlotIndex && this.controller.isTodayValue) {
      this.goLive();
      return;
    }

    const selectedTime = this.slotIndexToTime(slotIndex);
    this.controller.selectedTimestampValue = selectedTime.toISOString();
    this.updateTimeDisplay(selectedTime, isFuture);
    this.updateUrlTimeParam(slotIndex);
    this.fetchHistoricalActuals(selectedTime, { isFuture });
  }

  updateSliderTrackColors() {
    if (!this.controller.hasTimeSliderTarget || this.liveSlotIndex === undefined) return;

    const max = parseInt(this.controller.timeSliderTarget.max);
    const livePercent = (this.liveSlotIndex / max) * 100;

    const isDarkMode = window.matchMedia("(prefers-color-scheme: dark)").matches;
    const pastColor = isDarkMode ? "#4b5563" : "#d1d5db";
    const futureColor = isDarkMode ? "#22c55e" : "#4ade80";

    this.controller.timeSliderTarget.style.background = `linear-gradient(to right, ${pastColor} 0%, ${pastColor} ${livePercent}%, ${futureColor} ${livePercent}%, ${futureColor} 100%)`;
  }

  updateUrlTimeParam(slotIndex) {
    const totalMinutes = slotIndex * 15;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const hhmm = String(hours).padStart(2, "0") + String(minutes).padStart(2, "0");
    const url = new URL(window.location);
    url.searchParams.set("t", hhmm);
    history.replaceState(null, "", url);
  }

  parseTimeParam(timeParam, maxSlots) {
    if (!/^\d{3,4}$/.test(timeParam)) return null;

    const padded = timeParam.padStart(4, "0");
    const hours = parseInt(padded.slice(0, 2));
    const minutes = parseInt(padded.slice(2, 4));

    if (hours > 23 || minutes > 59) return null;

    const totalMinutes = hours * 60 + minutes;
    const slotIndex = Math.floor(totalMinutes / 15);

    return slotIndex <= maxSlots ? slotIndex : null;
  }

  removeUrlTimeParam() {
    const url = new URL(window.location);
    url.searchParams.delete("t");
    history.replaceState(null, "", url);
  }

  syncUrlParams(hasTimeParam) {
    const url = new URL(window.location);
    const currentT = hasTimeParam ? url.searchParams.get("t") : null;

    url.searchParams.delete("date");
    url.searchParams.delete("d");
    url.searchParams.delete("t");

    if (!this.controller.isTodayValue) {
      url.searchParams.set("d", this.controller.baseDateValue);
    }
    if (currentT) {
      url.searchParams.set("t", currentT);
    }

    history.replaceState(null, "", url);
  }

  slotIndexToTime(slotIndex) {
    const startOfDay = this.controller.sliderStartOfDay || new Date().setHours(0, 0, 0, 0);
    return new Date(startOfDay.getTime() + slotIndex * 15 * 60 * 1000);
  }

  async fetchHistoricalActuals(selectedTime, { isFuture = false } = {}) {
    if (this.historicalFetchController) {
      this.historicalFetchController.abort();
    }
    this.historicalFetchController = new AbortController();

    this.controller.unsubscribeFromChannel();
    this.controller.stopPolling();

    const url = new URL(this.controller.actualsUrlValue, window.location.origin);
    url.searchParams.set("timestamp", selectedTime.toISOString());

    try {
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        signal: this.historicalFetchController.signal,
      });

      if (response.status === 429) {
        this.scheduleHistoricalRetry(selectedTime, { isFuture });
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch historical actuals: ${response.status}`);
      }
      this.controller.actuals = await response.json();
      this.controller.updateStatusSuccess(selectedTime, { isHistorical: true, isFuture });
      this.controller.renderTree();
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("Error fetching historical actuals:", error);
      this.controller.updateStatusError();
    }
  }

  scheduleHistoricalRetry(selectedTime, { isFuture = false } = {}) {
    this.pendingHistoricalTime = selectedTime;
    this.pendingHistoricalIsFuture = isFuture;
    this.controller.scheduleRetry(
      () => {
        if (this.pendingHistoricalTime) {
          this.fetchHistoricalActuals(this.pendingHistoricalTime, { isFuture: this.pendingHistoricalIsFuture });
          this.pendingHistoricalTime = null;
          this.pendingHistoricalIsFuture = false;
        }
      },
      { timeoutProperty: "historicalRetryTimeout" },
    );
  }

  isViewingHistoricalData() {
    return this.controller.selectedTimestampValue && this.controller.selectedTimestampValue.length > 0;
  }

  goLive() {
    if (!this.controller.isTodayValue) {
      window.location.href = window.location.pathname;
      return;
    }

    this.controller.selectedTimestampValue = "";
    if (this.controller.hasTimeSliderTarget) {
      this.controller.timeSliderTarget.value = this.liveSlotIndex;
    }
    this.updateTimeDisplay(null);
    this.removeUrlTimeParam();
    this.controller.subscribeToChannel();
    this.controller.fetchActuals();
  }

  updateTimeDisplay(time, isFuture = false) {
    if (!this.controller.hasTimeDisplayTarget) return;

    const displayTime = time || new Date();

    this.controller.timeDisplayTarget.textContent = displayTime.toLocaleTimeString(navigator.language || undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });

    if (!this.controller.hasLiveButtonTarget) return;

    const button = this.controller.liveButtonTarget;

    if (!time) {
      button.textContent = this.controller.i18nLiveValue || "Live";
      button.dataset.timeState = "live";
    } else if (isFuture) {
      button.textContent = this.controller.i18nPlannedValue || "Planned";
      button.dataset.timeState = "planned";
    } else {
      button.textContent = this.controller.i18nRecordedValue || "Recorded";
      button.dataset.timeState = "recorded";
    }
  }
}
